export type EditCaret = {
  key: string;
  part: 'key' | 'op' | 'value' | 'operator' | 'type' | null;
  offset: number;
};

export type EditDraft = {
  key: string;
  text: string;
};

export type EditState = {
  query: string;
  drafts: EditDraft[];
};

export type EditKind = 'insert' | 'delete' | 'other';

type EditEntry = {
  state: EditState;
  kind: EditKind;
  caretBefore: EditCaret | null;
  caretAfter: EditCaret | null;
};

export type EditHistory = {
  entries: EditEntry[];
  index: number;
  canCoalesce: boolean;
};

export type EditRecord = {
  state: EditState;
  kind: EditKind;
  caretBefore: EditCaret | null;
  caretAfter: EditCaret | null;
};

export type EditRestore = {
  state: EditState;
  caret: EditCaret | null;
};

const MAX_ENTRIES = 100;

export function createEditHistory(state: EditState): EditHistory {
  return {
    entries: [{ state, kind: 'other', caretBefore: null, caretAfter: null }],
    index: 0,
    canCoalesce: false,
  };
}

export function sameEditState(a: EditState, b: EditState): boolean {
  if (a.query !== b.query || a.drafts.length !== b.drafts.length) {
    return false;
  }
  return a.drafts.every((draft, i) => draft.key === b.drafts[i]?.key && draft.text === b.drafts[i]?.text);
}

function sameField(a: EditCaret | null, b: EditCaret | null): boolean {
  return a !== null && b !== null && a.key === b.key && a.part === b.part;
}

function sameCaret(a: EditCaret | null, b: EditCaret | null): boolean {
  return sameField(a, b) && a?.offset === b?.offset;
}

export function recordEdit(history: EditHistory, record: EditRecord): EditHistory {
  const current = history.entries[history.index];
  if (current && sameEditState(current.state, record.state)) {
    return history;
  }
  const entries = history.entries.slice(0, history.index + 1);
  const top = entries[entries.length - 1];
  const extendsTop =
    history.canCoalesce &&
    record.kind !== 'other' &&
    top !== undefined &&
    top.kind === record.kind &&
    sameField(top.caretAfter, record.caretAfter) &&
    sameCaret(top.caretAfter, record.caretBefore);
  if (extendsTop) {
    entries[entries.length - 1] = { ...top, state: record.state, caretAfter: record.caretAfter };
  } else {
    entries.push({ ...record });
  }
  const overflow = Math.max(0, entries.length - MAX_ENTRIES);
  const trimmed = overflow > 0 ? entries.slice(overflow) : entries;
  return { entries: trimmed, index: trimmed.length - 1, canCoalesce: record.kind !== 'other' };
}

export function undoEdit(history: EditHistory): { history: EditHistory; restore: EditRestore } | null {
  if (history.index <= 0) {
    return null;
  }
  const undone = history.entries[history.index];
  const target = history.entries[history.index - 1];
  if (!undone || !target) {
    return null;
  }
  return {
    history: { ...history, index: history.index - 1, canCoalesce: false },
    restore: { state: target.state, caret: undone.caretBefore ?? target.caretAfter },
  };
}

export function redoEdit(history: EditHistory): { history: EditHistory; restore: EditRestore } | null {
  const target = history.entries[history.index + 1];
  if (!target) {
    return null;
  }
  return {
    history: { ...history, index: history.index + 1, canCoalesce: false },
    restore: { state: target.state, caret: target.caretAfter },
  };
}

export function editKindForInputType(inputType: string | undefined, grew: boolean): EditKind {
  if (!inputType) {
    return grew ? 'insert' : 'delete';
  }
  if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop' || inputType === 'deleteByCut') {
    return 'other';
  }
  if (inputType.startsWith('insert')) {
    return 'insert';
  }
  if (inputType.startsWith('delete')) {
    return 'delete';
  }
  return 'other';
}
